import { useState } from 'react';
import { useNavigate } from 'react-router';
import { STAGE_LABEL, casePath, type Stage } from '@/domain';
import { useBoard, useStaffReport } from '@/application/usecases/useBoard';
import { useLineLayout } from '@/application/usecases/useLineLayout';
import { percentChange, useReportSummary, useSalesReport } from '@/application/usecases/useArchive';
import { useStopWork } from '@/application/usecases/useKits';
import { useSession } from '@/application/usecases/useSession';
import type { AttentionRow, LiveWork } from '@/application/ports/BoardRepository';
import { formatDuration, useNow } from '@/application/timer/timerStore';
import { initials } from '@/shared/format/initials';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { archiveOfPeriod } from '@/features/reports/periodLinks';
import { ProductionLine } from '@/ui/data/ProductionLine';
import { StatusDot } from '@/ui/data/StatusDot';
import { Button } from '@/ui/primitives/Button';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { toast } from '@/ui/primitives/toastStore';
import { SkeletonBar, SkeletonCards, SkeletonRows } from '@/ui/feedback/Loading';
import { useCountUp } from '@/ui/motion/useCountUp';
import { WidgetSection } from '../WidgetSection';
import { useWidgetRange } from '../widgetBoardContext';
import board from '../WidgetBoard.module.css';
import styles from './Floor.module.css';

/**
 * The sections of the Overview, each a widget. Section 5.2, and the lab's
 * request of 12 October 2026, which made the Overview a screen its reader
 * arranges and asked that it stay as it is until they do.
 *
 * So nothing here is new. Each widget is the section the Overview drew, with
 * its markup, its words and its reserved space: the production line, the six
 * figures, the two sales cards, the running clocks, the attention list and the
 * team. What changed is that each asks for its own data, and is offered only
 * to a reader the server answers (`mayShowWidget`), so one can stand on a
 * Dashboard without the others.
 *
 * Every clock is driven by the ONE application interval, section 10 item 4:
 * with ten live cards, ten intervals would be ten timers fighting React on a
 * device that is also running a camera.
 */


/** Whether the reader holds a screen, for a figure or a row that leads to one. */
function useHolds(): (view: string) => boolean {
  const { data: session } = useSession();
  return (view) => session?.views.some((each) => each === view) === true;
}

/** Opens a case from the floor: the tray for whoever holds a bench screen, otherwise its order. */
function useOpenCase(): (jobOrderNo: string, kitId: string) => void {
  const navigate = useNavigate();
  const { data: session } = useSession();
  return (jobOrderNo, kitId) => {
    const path = casePath(session?.views ?? [], { jobOrderNo, kitId }, 'kit');
    if (path !== null) void navigate(path);
  };
}

/**
 * The one sentence over a screen whose board could not be read. Once, above
 * the widgets, however many of them read the board: it used to stand at the
 * top of the Overview, and three widgets saying it would be three.
 */
export function BoardFailure() {
  const boardQuery = useBoard();
  return boardQuery.isError ? <p className={board.hint}>{strings.errors.server}</p> : null;
}

/**
 * The production line: what a manager opens the Overview to see. Request of
 * 3 October 2026, section 3. Its slot keeps the height of the panel while the
 * board loads, so nothing under it jumps when it arrives.
 *
 * The panel is far taller drawn top to bottom than left to right, so the slot
 * holds the height of the layout this device remembers, and the skeleton is
 * held to that height too. The request of 5 October 2026 (third), section 9.
 */
export function LineWidget() {
  const navigate = useNavigate();
  const boardQuery = useBoard();
  // Left to right or top to bottom: one choice, shared with the Production line screen.
  const { vertical, setVertical } = useLineLayout();

  return (
    <WidgetSection label={strings.line.label} title={strings.line.title} hint={strings.line.hint}>
      <div
        className={`${vertical ? styles.lineSlotVertical : styles.lineSlot} ${boardQuery.isPending ? styles.lineSlotWaiting : ''}`}
      >
        {boardQuery.isPending && <SkeletonCards cards={6} label={strings.common.loadingBoard} />}
        {boardQuery.data !== undefined && (
          <ProductionLine
            stages={boardQuery.data.stages}
            activeKits={boardQuery.data.activeKits}
            onOpenStage={(stage: Stage) => void navigate(`/line/${stage}`)}
            logistics={boardQuery.data.logistics}
            onOpenLogistics={() => void navigate('/line/logistics')}
            vertical={vertical}
            onVerticalChange={setVertical}
          />
        )}
      </div>
    </WidgetSection>
  );
}

/**
 * The lab in six figures.
 *
 * Rendered whether or not the figures have arrived, with the value blank until
 * they do. This is the layout shift fix and it is worth stating plainly:
 * mounting this strip only once the data lands pushed everything below it down
 * the page, and Lighthouse measured a cumulative layout shift of 0.477 for it.
 * A manager reaching for a row watches it move out from under their finger.
 * Six cards of a fixed shape reserve the space from the first paint, so the
 * arriving numbers change text and nothing else.
 *
 * Each card opens the list of exactly the cases it counts. The request of
 * 7 October 2026, section 7. Active, Due and Overdue are the Orders list:
 * the cases still being made (the lab's request of 17 October 2026, item 3),
 * due today, and overdue, all three asked of the server by the one rule the
 * figure itself is counted by. On time, Turnaround and Completed are
 * all figures of the cases that left in the period, so each opens the Archive
 * narrowed to those days. A card is a button only for a reader who holds the
 * screen it opens.
 */
export function FiguresWidget() {
  const navigate = useNavigate();
  const range = useWidgetRange();
  const holds = useHolds();
  const summary = useReportSummary(range);

  const toOrders = (search: string) =>
    holds('orders') ? () => void navigate({ pathname: '/orders', search }) : undefined;
  // The days the period covers, as the report counts them; All time is the whole archive.
  const toArchive = holds('archive') ? () => void navigate(archiveOfPeriod(range)) : undefined;

  return (
    <div className={styles.kpis} role="group" aria-label={strings.overview.figuresLabel}>
      <Kpi label={strings.reports.active} value={summary.data?.active} onPress={toOrders('?status=active')} />
      <Kpi label={strings.reports.due} value={summary.data?.due} onPress={toOrders('?due=today')} />
      <Kpi
        label={strings.reports.overdue}
        value={summary.data?.overdue}
        alert={(summary.data?.overdue ?? 0) > 0}
        onPress={toOrders('?due=overdue')}
      />
      <Kpi
        label={strings.reports.onTime}
        value={summary.data === undefined ? undefined : `${summary.data.onTimePercent}%`}
        onPress={toArchive}
      />
      <Kpi
        label={strings.reports.turnaround}
        value={
          summary.data === undefined
            ? undefined
            : `${summary.data.averageTurnaroundDays}${strings.reports.days}`
        }
        onPress={toArchive}
      />
      <Kpi label={strings.reports.completed} value={summary.data?.completed} onPress={toArchive} />
    </div>
  );
}

/**
 * The two metrics the client named, kept in front of a manager who is sent
 * money. Both cards always present, for the same reason as the figures.
 *
 * Offered to a holder of Reports who is sent money, which is who the server
 * answers the sales report to. It used to be drawn for anybody sent money, and
 * somebody given the Overview without Reports watched two cards wait for good.
 */
export function SalesWidget() {
  const navigate = useNavigate();
  const range = useWidgetRange();
  const sales = useSalesReport({ range });
  // Each card opens the report it is cut from. The lab's request of 17 October 2026, item 1.
  const toReports = () => void navigate('/reports');

  const delta =
    sales.data === undefined ? null : percentChange(sales.data.unitsSold, sales.data.previousUnitsSold);

  return (
    <WidgetSection
      label={strings.overview.reporting}
      title={strings.overview.analytics}
      action={
        <Button variant="text" onClick={() => void navigate('/reports')}>
          {strings.overview.openReports}
        </Button>
      }
    >
      <div className={styles.pair}>
        <button type="button" className={`${styles.card} ${styles.cardPress}`} onClick={toReports}>
          <h3>{strings.reports.unitsSold}</h3>
          <div className={styles.big}>
            {sales.data === undefined ? (
              <SkeletonBar width="44%" height={26} />
            ) : (
              sales.data.unitsSold.toLocaleString()
            )}
          </div>
          <p className={styles.sub}>
            {sales.data === undefined
              ? ''
              : delta === null
                ? strings.reports.noPrevious
                : `${delta > 0 ? '+' : ''}${delta}% ${strings.reports.versusPrevious}`}
          </p>
        </button>
        <button type="button" className={`${styles.card} ${styles.cardPress}`} onClick={toReports}>
          <h3>{strings.reports.bestSeller}</h3>
          <div className={styles.bigText}>
            {sales.data === undefined ? (
              <SkeletonBar width="62%" height={22} />
            ) : (
              (sales.data.bestSeller ?? strings.reports.noData)
            )}
          </div>
          <p className={styles.sub}>
            {sales.data?.byItem[0] === undefined
              ? ''
              : `${sales.data.byItem[0].units} ${strings.archive.units.toLowerCase()}`}
          </p>
        </button>
      </div>
    </WidgetSection>
  );
}

/**
 * Who is timing a task right now, each with a running clock.
 *
 * Stopping somebody else's clock, from the chip that shows it running. Request
 * of 25 September 2026, behind a permission the lab can take back without a
 * deploy. The confirmation names the person and the case, because a wall
 * display is where this is pressed and a wrong tap there stops the wrong clock.
 *
 * It is the shared confirmation, with the running clock inside it: the request
 * of 5 October 2026 (third), section 2. It always was a step of its own, so it
 * asks whoever holds the permission and never a second time.
 */
export function LiveWorkWidget() {
  const { data: session } = useSession();
  const boardQuery = useBoard();
  const openCase = useOpenCase();
  const canStop = session?.permissions.includes('stopWork') === true;
  const stopWork = useStopWork();
  const [stopping, setStopping] = useState<LiveWork | null>(null);

  function stop(entry: LiveWork) {
    stopWork.mutate(
      { kitId: entry.kitId },
      {
        onSuccess: (result) => {
          setStopping(null);
          toast(
            `${result.historyEntry.byUserName ?? entry.userName} · ${entry.jobOrderNo} ${strings.overview.stopped} ${formatDuration(result.historyEntry.activeSeconds)}`,
          );
        },
        onError: (error: Error) => {
          setStopping(null);
          toast(failureWords(error, strings.overview.stopFailed));
        },
      },
    );
  }

  return (
    <WidgetSection
      label={strings.overview.rightNow}
      title={strings.overview.liveWork}
      hint={strings.overview.liveWorkHint}
    >
      {boardQuery.isPending && <SkeletonCards cards={3} label={strings.common.loadingBoard} />}

      {boardQuery.data !== undefined &&
        (boardQuery.data.liveWork.length === 0 ? (
          <Empty heading={strings.overview.noLiveWork} detail={strings.overview.noLiveWorkDetail} />
        ) : (
          <div className={styles.liveGrid}>
            {/*
              Keyed by the tray, not the person: one person may be timing three
              trays, which is three chips, and three chips under one key drew one.
            */}
            {boardQuery.data.liveWork.map((entry) => (
              <LiveCard
                key={entry.kitId}
                entry={entry}
                onOpen={() => openCase(entry.jobOrderNo, entry.kitId)}
                onStop={canStop ? () => setStopping(entry) : undefined}
              />
            ))}
          </div>
        ))}

      {stopping !== null && (
        <ConfirmSheet
          title={`${strings.overview.stopTitle} ${stopping.jobOrderNo}?`}
          statement={strings.confirm.stopClock(stopping.userName, stopping.jobOrderNo)}
          confirmLabel={strings.overview.stopConfirm}
          cancelLabel={strings.overview.stopCancel}
          closeLabel={strings.sheet.close}
          busy={stopWork.isPending}
          onCancel={() => setStopping(null)}
          onConfirm={() => stop(stopping)}
        >
          <p className={styles.stopLead}>
            {stopping.userName} · {STAGE_LABEL[stopping.stage]}. {strings.overview.stopLead}
          </p>
          <LiveClock startedAt={stopping.startedAt} />
        </ConfirmSheet>
      )}
    </WidgetSection>
  );
}

/** The cases that need somebody: urgent, overdue or in rework. A row opens its case. */
export function AttentionWidget() {
  const boardQuery = useBoard();
  const openCase = useOpenCase();

  return (
    <WidgetSection
      label={strings.overview.action}
      title={strings.overview.attention}
      hint={strings.overview.attentionHint}
    >
      {boardQuery.isPending && <SkeletonRows rows={4} label={strings.common.loadingBoard} />}

      {boardQuery.data !== undefined &&
        (boardQuery.data.attention.length === 0 ? (
          <Empty heading={strings.overview.allClear} detail={strings.overview.allClearDetail} />
        ) : (
          <ul className={styles.attention} aria-label={strings.overview.attention}>
            {boardQuery.data.attention.slice(0, 8).map((row) => (
              <AttentionItem key={row.kitId} row={row} onOpen={() => openCase(row.jobOrderNo, row.kitId)} />
            ))}
          </ul>
        ))}
    </WidgetSection>
  );
}

/**
 * The team today: the bench, four of them, with the way to everybody's.
 *
 * The team's figures are the Staff productivity screen's, sent to whoever
 * holds it, whatever their role, and offered to nobody else: the lab's request
 * of 11 October 2026.
 */
export function TeamWidget() {
  const navigate = useNavigate();
  const range = useWidgetRange();
  const staff = useStaffReport(range);

  return (
    <WidgetSection
      label={strings.overview.people}
      title={strings.overview.team}
      hint={strings.overview.teamHint}
      action={
        <Button variant="text" onClick={() => void navigate('/staff')}>
          {strings.overview.viewAll}
        </Button>
      }
    >
      {staff.isPending && <SkeletonCards cards={4} label={strings.common.loadingUsers} />}

      {staff.data !== undefined && (
        <div className={styles.team}>
          {/* The bench: reception is on the report for the cases it took in, not here. */}
          {staff.data.rows
            .filter((person) => person.benchWork)
            .slice(0, 4)
            .map((person) => (
              <div key={person.userId} className={styles.member}>
                <div className={styles.memberHead}>
                  <span className={styles.avatar}>{initials(person.name)}</span>
                  <b>{person.name}</b>
                </div>
                <Row label={strings.overview.tasksDone} value={String(person.tasksCompleted)} />
                <Row label={strings.perf.teethWorked} value={String(person.teethWorked)} />
                <Row label={strings.overview.timeWorked} value={formatDuration(person.activeSeconds)} />
                <Row
                  label={strings.overview.averagePerTask}
                  value={formatDuration(Math.round(person.averageSecondsPerTask))}
                />
              </div>
            ))}
        </div>
      )}
    </WidgetSection>
  );
}

/** A strip of figures, each a card. The Overview's six, and a Dashboard's counts of cases. */
export function KpiStrip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.kpis} role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function Kpi({
  label,
  value,
  alert = false,
  onPress,
}: {
  label: string;
  /** Undefined while the figure is loading. The card still takes its space. Null for one that could not be read. */
  value?: string | number | null;
  alert?: boolean;
  /** Opens the cases the figure counts. Absent, the card is a figure to read. */
  onPress?: () => void;
}) {
  // A count that changes counts to its new value; a figure in words just changes.
  const counted = useCountUp(typeof value === 'number' ? value : undefined);
  const shown = typeof value === 'number' ? counted : value;
  const content = (
    <>
      <span className={styles.l}>{label}</span>
      <span className={`${styles.v} ${alert ? styles.alertValue : ''}`}>
        {shown === null ? '' : (shown ?? <SkeletonBar width="52%" height={18} />)}
      </span>
    </>
  );
  if (onPress === undefined) return <div className={styles.kpi}>{content}</div>;
  return (
    <button
      type="button"
      className={`${styles.kpi} ${styles.kpiPress}`}
      title={strings.overview.openCounted}
      onClick={onPress}
    >
      {content}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.memberRow}>
      {label} <b>{value}</b>
    </div>
  );
}

function Empty({ heading, detail }: { heading: string; detail: string }) {
  return (
    <div className={styles.empty}>
      <h3>{heading}</h3>
      <p>{detail}</p>
    </div>
  );
}

/** A clock since an instant, read from the shared application tick. */
function LiveClock({ startedAt, tone }: { startedAt: string; tone?: 'watch' }) {
  const now = useNow();
  const seconds = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));
  return (
    <span className={`${styles.clock} ${tone === 'watch' ? styles.watch : ''}`}>
      {formatDuration(seconds)}
    </span>
  );
}

/**
 * One running task, one clock. The card opens the case; the Stop beside it, for
 * a holder of `stopWork`, stops the clock on the holder's behalf. Two buttons
 * rather than one, because a button inside a button is not HTML and a card that
 * both opened and stopped would do the wrong one on a phone.
 *
 * A clock that has run longer than a working day, section 1.3 of the request of
 * 20 September 2026, reads in watch tone and says so: nobody has stopped it, and
 * this is where the next person to look notices rather than the report a month
 * later.
 */
function LiveCard({
  entry,
  onOpen,
  onStop,
}: {
  entry: LiveWork;
  onOpen: () => void;
  onStop?: () => void;
}) {
  return (
    <div className={`${styles.live} ${entry.overrunning ? styles.overrunning : ''}`}>
      <button className={styles.liveOpen} onClick={onOpen}>
        <span className={styles.liveHead}>
          <span className={styles.avatar}>{initials(entry.userName)}</span>
          <span className={styles.liveWho}>
            <b>{entry.userName}</b>
            <span>{STAGE_LABEL[entry.stage]}</span>
          </span>
        </span>
        <LiveClock startedAt={entry.startedAt} tone={entry.overrunning ? 'watch' : undefined} />
        <span className={styles.liveJob}>
          <span className={styles.mono}>{entry.jobOrderNo}</span> · {entry.product}
        </span>
        {entry.overrunning && (
          <span className={styles.liveNote} role="status">
            {strings.overview.overrunning}
          </span>
        )}
      </button>
      {onStop !== undefined && (
        <button
          type="button"
          className={styles.liveStop}
          onClick={onStop}
          aria-label={`${strings.overview.stop} ${entry.jobOrderNo}`}
        >
          {strings.overview.stop}
        </button>
      )}
    </div>
  );
}

const REASON_TONE = { urgent: 'urgent', overdue: 'late', rework: 'rework' } as const;
const REASON_LABEL = {
  urgent: strings.overview.reasonUrgent,
  overdue: strings.overview.reasonOverdue,
  rework: strings.overview.reasonRework,
} as const;

function AttentionItem({ row, onOpen }: { row: AttentionRow; onOpen: () => void }) {
  // Every reason the case is here, loudest first; the one, from an API that sends no more.
  const reasons = row.reasons ?? [row.reason];
  // A case can be a day late with no working day gone, over a weekend: late is never "due in 0".
  const due =
    row.daysLeft < 0 || reasons.includes('overdue')
      ? `${strings.overview.overdueBy} ${Math.max(1, Math.abs(row.daysLeft))}${strings.overview.daysShort}`
      : `${strings.overview.dueIn} ${row.daysLeft}${strings.overview.daysShort}`;

  return (
    <li>
      <button className={styles.attentionRow} onClick={onOpen}>
        <span className={styles.attentionWhy}>
          {reasons.map((reason) => (
            <StatusDot key={reason} tone={REASON_TONE[reason]}>
              {REASON_LABEL[reason]}
            </StatusDot>
          ))}
        </span>
        <span className={styles.attentionWhat}>
          <span className={styles.mono}>{row.jobOrderNo}</span> · {row.product}
        </span>
        <span className={styles.attentionWhere}>
          {STAGE_LABEL[row.stage]} · {row.assignedToUserName ?? strings.stage.unassigned}
        </span>
        <span className={styles.attentionDue}>{due}</span>
      </button>
    </li>
  );
}
